import type { Communication } from '@medplum/fhirtypes';
import {
  addZonedDays,
  formatUsDateLong,
  getZonedDateTimeParts,
} from '../../lib/dateHelpers';

/**
 * `Communication.sent` / `.received` are absolute instants, so message timestamps
 * and date separators render in the viewer's display zone (Practitioner preference,
 * else browser) rather than UTC or the browser zone.
 */

/** Civil day of an instant in `timeZone`, as `YYYY-MM-DD`. */
export function zonedDateKey(date: Date, timeZone: string): string {
  const p = getZonedDateTimeParts(date, timeZone);
  return `${p.year}-${String(p.month).padStart(2, '0')}-${String(p.day).padStart(2, '0')}`;
}

export function groupMessagesByDate(
  messages: Communication[],
  timeZone: string,
): { date: string; messages: Communication[] }[] {
  const groups: Map<string, Communication[]> = new Map();
  messages.forEach((msg) => {
    const dateKey = msg.sent ? zonedDateKey(new Date(msg.sent), timeZone) : 'unknown';
    if (!groups.has(dateKey)) groups.set(dateKey, []);
    groups.get(dateKey)!.push(msg);
  });
  return Array.from(groups.entries()).map(([date, msgs]) => ({ date, messages: msgs }));
}

/** `dateKey` is a `YYYY-MM-DD` civil day from `groupMessagesByDate`. */
export function formatDateHeader(dateKey: string, timeZone: string, now: Date = new Date()): string {
  if (dateKey === zonedDateKey(now, timeZone)) return 'Today';
  if (dateKey === zonedDateKey(addZonedDays(now, -1, timeZone), timeZone)) return 'Yesterday';
  return formatUsDateLong(dateKey);
}

export function formatMessageTime(dateStr: string, timeZone: string): string {
  try {
    return new Date(dateStr).toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
      timeZone,
    });
  } catch {
    return new Date(dateStr).toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    });
  }
}
